import styles from './MarketHealth.module.css';
import { STRATEGY_CONFIGS } from './strategyConfigs';

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Model-state chip. Shows the strategy's current position, since-date,
 * trade-frequency context, and a one-sentence explanation that varies by day
 * type. Never uses "buy," "sell," or "recommend."
 *
 * The chip reports the current position (the authoritative Invested/Cash
 * signal) AND the exact canonical risk score with its threshold, side by side
 * but as INDEPENDENT facts. The copy never infers the position from the score's
 * direction — the score sitting above or below its threshold is context, not a
 * cause of the position, and crossing the threshold is never described as
 * forcing a trade.
 *
 * `signal` is `payload.current.signals[mode]` (lag-resolved) —
 * `{ position, signal: 'BUY'|'SELL'|'HOLD', sinceDate, daysSinceLastTrade }`.
 * `score` is the exact canonical risk score (higher = more risk), shown
 * unchanged. `mode` selects the strategy's threshold label from config;
 * `riskBoundaryLabel` may be passed directly to override it.
 * `triggerInfo` (optional) drives the compact price-gate summary rendered in
 * the right column alongside `children` (the TriggerPipeline gauge) — gate
 * active/inactive plus the distance percentage, never the trigger identity.
 * `children` (optional) render in the right column beneath the gate summary —
 * this is where the caller places the TriggerPipeline proximity gauge so the
 * whole price-gate unit sits NEXT TO the position status.
 */
const ModelState = ({ signal, score, mode, riskBoundaryLabel, triggerInfo, children }) => {
  if (!signal) return null;

  const { position, signal: signalWord, sinceDate, daysSinceLastTrade } = signal;
  const isTransition = signalWord === 'BUY' || signalWord === 'SELL';
  const isInvested = position === 'invested';

  // Compact price-gate summary shown at the right of the headline row. Reports
  // whether the gate is active and how much clearance SPY has (the distance
  // percentage) WITHOUT revealing what the gate is measured against — the
  // trigger identity stays proprietary. `null` when no numeric distance.
  let gateSummary = null;
  const distancePct = triggerInfo?.distancePct;
  if (typeof distancePct === 'number' && Number.isFinite(distancePct)) {
    const gateActive = triggerInfo?.active === true;
    const absDist = Math.abs(distancePct).toFixed(1);
    const gateClass = [
      styles.modelStateGate,
      gateActive ? styles.modelStateGateActive : styles.modelStateGateSafe,
    ].join(' ');
    gateSummary = (
      <span className={gateClass} data-testid="model-state-gate">
        {gateActive
          ? `Price gate active · ${absDist}% past`
          : `Price gate inactive · ${absDist}% clearance`}
      </span>
    );
  }

  const thresholdLabel = riskBoundaryLabel
    ?? STRATEGY_CONFIGS[mode]?.riskBoundaryLabel
    ?? null;

  const scoreText = isFiniteNumber(score) ? String(score) : 'unavailable';

  // Format since-date for display.
  const sinceDateFormatted = sinceDate
    ? new Date(sinceDate + 'T00:00:00').toLocaleDateString(undefined, { dateStyle: 'medium' })
    : '—';

  // A single factual "score line" shown on every day type: the exact risk
  // score and (when known) its threshold. Deliberately neutral — it states
  // where the score is, never what it means for the position.
  const scoreLine = thresholdLabel
    ? `Today's risk score is ${scoreText} (${thresholdLabel}).`
    : `Today's risk score is ${scoreText}.`;

  // Day-type copy. Transition days describe WHAT changed; hold days show
  // only the score line -- the TriggerPipeline component now carries the
  // proximity/confirmation context that the old editorial paragraph covered.
  let explanation;
  if (isTransition && !isInvested) {
    explanation = `${scoreLine} The model moved to Cash today. This is its rules-based response to broad structural stress.`;
  } else if (isTransition && isInvested) {
    explanation = `${scoreLine} The model returned to Invested today. Re-entry is deliberately fast because historical recoveries have often been V-shaped.`;
  } else {
    explanation = scoreLine;
  }

  const stateModifier = isTransition
    ? (isInvested ? styles.modelStateTransitionInvested : styles.modelStateTransitionCash)
    : (isInvested ? styles.modelStateInvested : styles.modelStateCash);

  const containerClassName = [
    styles.modelState,
    isTransition ? styles.modelStateTransition : '',
    stateModifier,
  ].filter(Boolean).join(' ');

  const dotClassName = [
    styles.modelStateDot,
    isInvested ? styles.modelStateDotInvested : styles.modelStateDotCash,
  ].filter(Boolean).join(' ');

  // Right column holds the whole price-gate unit — the summary text plus the
  // proximity gauge (passed as children) — so it sits NEXT TO the position
  // status rather than stacked below it. Rendered only when there's something
  // to show, so plain-position cards keep the position status full width.
  const hasGate = gateSummary != null || children != null;

  return (
    <div className={containerClassName} role="status" aria-live="polite" data-testid="model-state">
      <div className={styles.modelStateRow}>
        <div className={styles.modelStateMain}>
          <p className={styles.modelStateHeadline}>
            <span className={dotClassName} aria-hidden="true">●</span>
            <strong>{isInvested ? 'INVESTED' : 'CASH'}</strong>
            <span className={styles.modelStateMeta}>
              since {sinceDateFormatted}
              {typeof daysSinceLastTrade === 'number' && ` (${daysSinceLastTrade} days)`}
            </span>
          </p>
          <p className={styles.modelStateExplanation}>{explanation}</p>
        </div>
        {hasGate && (
          <div className={styles.modelStateGateColumn}>
            {gateSummary}
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelState;
